import { Identifiable } from 'gf-types';
import { FieldTypes } from '../../../../helpers/constants';
import { getIn, setIn } from '../../../../helpers/helpers';
import { getGuid } from '../../../../helpers/utility';
import { DataLayoutPosition, FieldPosition, ItemCollectionItem, TemplateField } from '../../../../types/template-types';
import { getParentKeyPath } from '../../../helpers';
import {
  getDataLayoutPositionItemKeyPathsByCheckBoxItemId,
  getDataLayoutPositionKeyPathsByFieldId,
  getFieldPositionKeyPathsByCheckBoxItemId,
  getFieldPositionKeyPathsByFieldId
} from '../../../selectors';
import { KeyPath, TemplateState } from '../../../templateeditor-types';
import { deleteItems } from '../../../updaters';
import { reorderItems } from '../../../utility';
import { analyticsTrack } from '../../../../helpers/AnalyticsUtility';

/**
 * Creates a new checkbox group field with the given name and adds items based on the original checkbox fields
 */
export function createCheckboxGroupField(groupName: string, originalCheckboxFields: TemplateField[]): TemplateField {
  const newCheckboxGroupId = getGuid();
  const checkboxGroupField: TemplateField = {
    id: newCheckboxGroupId,
    name: groupName,
    type: FieldTypes.CheckBoxGroup,
    allowOther: false,
    isFieldCommitted: false,
    isAiPlaced: false,
    items: []
  };

  // Create items from the original checkbox fields
  let sortNumber = 1;
  for (const checkBoxField of originalCheckboxFields) {
    const newItem: ItemCollectionItem = {
      id: checkBoxField.id, // keep the original checkbox field ID
      displayName: checkBoxField.name || `Checkbox ${sortNumber}`,
      value: checkBoxField.name || `Checkbox ${sortNumber}`,
      fieldId: newCheckboxGroupId, // Link item back to the new checkboxgroup field ID
      sortNumber: sortNumber
    };

    checkboxGroupField.items.push(newItem);
    sortNumber++;
  }

  return checkboxGroupField;
}

/**
 * Updates field positions to point to a checkbox group
 * Works for both new groups and adding to existing groups
 */
export function updateFieldPositions(state: TemplateState, checkboxFields: TemplateField[], checkboxGroupId: string): void {
  for (const checkboxField of checkboxFields) {
    const fieldPositionKeyPaths = getFieldPositionKeyPathsByFieldId(state, checkboxField.id);

    for (const fpKeyPath of fieldPositionKeyPaths) {
      const fieldPosition = getIn<FieldPosition>(state, fpKeyPath);
      if (!fieldPosition) continue;

      // Update field position to point to the group
      const updatedFieldPosition: FieldPosition = {
        ...fieldPosition,
        fieldId: checkboxGroupId,
        itemId: checkboxField.id // Use checkbox ID as the itemId in the group
      };

      setIn(state, fpKeyPath, updatedFieldPosition);
    }
  }
}

/**
 * Updates data layout positions for a new checkbox group
 * Keeps first position and updates it to point to the group, then removes the rest
 */
function updateDataLayoutPositionsForNewGroup(state: TemplateState, checkboxFields: TemplateField[], checkboxGroupField: TemplateField): TemplateState {
  const dlpKeyPathsByCheckbox = checkboxFields.flatMap((checkbox) => getDataLayoutPositionKeyPathsByFieldId(state, checkbox.id));

  if (dlpKeyPathsByCheckbox?.length > 0) {
    // Keep the first DataLayoutPosition and change it to the new checkbox group
    const [dlpToModifyKeyPath, ...dlpKeyPathsToDelete] = dlpKeyPathsByCheckbox;

    const dlpToUpdate = getIn<DataLayoutPosition>(state, dlpToModifyKeyPath);
    if (dlpToUpdate) {
      const updatedDlp: DataLayoutPosition = {
        ...dlpToUpdate,
        fieldId: checkboxGroupField.id,
        checkBoxGroupItems: checkboxGroupField.items.map((item) => ({
          id: getGuid(), // New DataLayoutPositionItemId
          itemId: item.id // References the checkbox field item ID
        }))
      };
      setIn(state, dlpToModifyKeyPath, updatedDlp);
    }

    // Remove other checkboxes after they're added to the group
    if (dlpKeyPathsToDelete.length > 0) {
      state = deleteItems(state, dlpKeyPathsToDelete);
    }
  }

  return state;
}

/**
 * Adds a checkbox field to an existing group's data layout position
 */
function addCheckboxToExistingGroupDlp(state: TemplateState, parentSectionKeyPath: KeyPath, existingGroupDlpIndex: number, checkboxFieldId: string): void {
  const existingGroupDlpPath = [...parentSectionKeyPath, existingGroupDlpIndex];
  const existingGroupDlp = getIn<DataLayoutPosition>(state, existingGroupDlpPath);

  // Add the checkbox as a checkBoxGroupItem
  if (existingGroupDlp) {
    existingGroupDlp.checkBoxGroupItems = existingGroupDlp.checkBoxGroupItems || [];
    existingGroupDlp.checkBoxGroupItems.push({
      id: getGuid(),
      itemId: checkboxFieldId
    });

    setIn(state, existingGroupDlpPath, existingGroupDlp);
  }
}

/**
 * Creates a new data layout position for a checkbox group
 */
function createNewGroupDataLayoutPosition(
  state: TemplateState,
  parentSectionKeyPath: KeyPath,
  checkboxGroupFieldId: string,
  sortNumber: number,
  checkboxFieldId: string
): void {
  // Create a new DataLayoutPosition for the group
  const newGroupDlp: DataLayoutPosition = {
    id: getGuid(),
    fieldId: checkboxGroupFieldId,
    sortNumber: sortNumber,
    checkBoxGroupItems: [
      {
        id: getGuid(),
        itemId: checkboxFieldId
      }
    ]
  };

  // Add the new DLP to the parent section
  const positions = getIn<DataLayoutPosition[]>(state, parentSectionKeyPath) || [];
  positions.push(newGroupDlp);
  setIn(state, parentSectionKeyPath, positions);
}

/**
 * Updates data layout positions for adding checkboxes to an existing group
 */
function updateDataLayoutPositionsForExistingGroup(state: TemplateState, checkboxFields: TemplateField[], checkboxGroupField: TemplateField): TemplateState {
  const checkboxFieldIds = checkboxFields.map((field) => field.id);

  for (const checkboxFieldId of checkboxFieldIds) {
    const dlpKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, checkboxFieldId);
    const dlpKeyPathsToDelete: KeyPath[] = [];

    for (const dlpKeyPath of dlpKeyPaths) {
      const dlp = getIn<DataLayoutPosition>(state, dlpKeyPath);
      if (!dlp) continue;

      // Find an existing DataLayoutPosition for the group, or create one
      const parentSectionKeyPath = getParentKeyPath(dlpKeyPath);
      const positions = getIn<DataLayoutPosition[]>(state, parentSectionKeyPath) || [];
      const existingGroupDlpIndex = positions.findIndex((pos) => pos.fieldId === checkboxGroupField.id);

      if (existingGroupDlpIndex >= 0) {
        // Add this checkbox to an existing group DLP
        addCheckboxToExistingGroupDlp(state, parentSectionKeyPath, existingGroupDlpIndex, checkboxFieldId);
      } else {
        // Create a new DataLayoutPosition for the group in this section
        createNewGroupDataLayoutPosition(state, parentSectionKeyPath, checkboxGroupField.id, dlp.sortNumber, checkboxFieldId);
      }

      // Mark this DLP for deletion
      dlpKeyPathsToDelete.push(dlpKeyPath);
    }

    // Remove the original checkbox DLPs
    if (dlpKeyPathsToDelete.length > 0) {
      state = deleteItems(state, dlpKeyPathsToDelete);
    }
  }

  return state;
}

/**
 * Updates data layout positions for checkbox groups
 * Handles both creating new groups and adding to existing groups
 */
export function updateDataLayoutPositions(
  state: TemplateState,
  checkboxFields: TemplateField[],
  checkboxGroupField: TemplateField,
  isNewGroup = true
): TemplateState {
  if (isNewGroup) {
    return updateDataLayoutPositionsForNewGroup(state, checkboxFields, checkboxGroupField);
  } else {
    return updateDataLayoutPositionsForExistingGroup(state, checkboxFields, checkboxGroupField);
  }
}

/**
 * Updates the template fields by replacing original checkboxes with the new checkbox group
 */
export function updateTemplateFieldState(state: TemplateState, originalCheckboxFields: TemplateField[], checkboxGroupField: TemplateField): void {
  //Filter out the original checkbox fields when updating state
  const updatedFields = state.template.fields.filter((field) => originalCheckboxFields.every((originalField) => originalField.id !== field.id));

  updatedFields.push(checkboxGroupField);
  state.template.fields = updatedFields;
}

/**
 * Gets original checkbox fields from the keyPaths
 */
export function getOriginalCheckboxFields(state: TemplateState, keyPaths: KeyPath[]): TemplateField[] {
  if (!keyPaths?.length) {
    return [];
  }
  return keyPaths.map((keyPath) => getIn<TemplateField>(state, keyPath));
}

/**
 * Separates checkbox group field and individual checkbox fields from the keyPaths
 */
export function separateCheckboxGroupAndFields(
  state: TemplateState,
  keyPaths: KeyPath[]
): {
  checkboxGroupFieldKeyPath: KeyPath | null;
  checkboxFields: TemplateField[];
} {
  let checkboxGroupFieldKeyPath: KeyPath | null = null;
  const checkboxFields: TemplateField[] = [];

  for (const keyPath of keyPaths) {
    const field = getIn<TemplateField>(state, keyPath);
    if (!field) continue;

    if (field.type === FieldTypes.CheckBoxGroup) {
      checkboxGroupFieldKeyPath = keyPath;
    } else if (field.type === FieldTypes.CheckBox) {
      checkboxFields.push(field);
    }
  }

  return { checkboxGroupFieldKeyPath, checkboxFields };
}

/**
 * Creates checkbox items for a checkbox group from individual checkbox fields
 */
export function createCheckboxItemsForGroup(checkBoxGroupField: TemplateField, checkboxFields: TemplateField[]): ItemCollectionItem[] {
  let nextSortNumber = checkBoxGroupField.items.length + 1;

  // Create group items from checkbox fields
  return checkboxFields.map((checkboxField) => {
    const newItem: ItemCollectionItem = {
      id: checkboxField.id,
      displayName: checkboxField.name || `Checkbox ${nextSortNumber}`,
      value: checkboxField.name || `Checkbox ${nextSortNumber}`,
      fieldId: checkBoxGroupField.id,
      sortNumber: nextSortNumber++
    };
    return newItem;
  });
}

/**
 * Finds items that were present in oldItems but not in newItems
 */
export function findRemovedItems(oldItems: Identifiable[], newItems: Identifiable[]): Identifiable[] {
  if (oldItems?.length === 0) return [];

  //return checkbox items that are not found in newItems
  return oldItems.filter((oldItem) => !newItems.some((newItem) => newItem.id === oldItem.id));
}

/**
 * Processes a removed checkbox item by cleaning up related field positions and data layout positions
 */
export function processRemovedCheckboxItem(state: TemplateState, removedItem: Identifiable): TemplateState {
  // Clean up field positions
  const fieldPositionKeyPaths = getFieldPositionKeyPathsByCheckBoxItemId(state, removedItem.id);
  if (fieldPositionKeyPaths.length > 0) {
    state = deleteItems(state, fieldPositionKeyPaths);
  }

  // Clean up data layout positions
  const dataLayoutPositionItemKeyPaths = getDataLayoutPositionItemKeyPathsByCheckBoxItemId(state, removedItem.id);
  if (dataLayoutPositionItemKeyPaths.length > 0) {
    state = deleteItems(state, dataLayoutPositionItemKeyPaths);

    // Clean up orphaned data layout positions
    state = removeUnmappedDataLayoutPositions(state, dataLayoutPositionItemKeyPaths);
  }

  return state;
}

/**
 * Cleans up any data layout positions that no longer reference any items
 */
export function removeUnmappedDataLayoutPositions(state: TemplateState, dataLayoutPositionItemKeyPaths: KeyPath[]): TemplateState {
  const dataLayoutPositionKeyPathsToDelete: KeyPath[] = [];

  for (const itemKeyPath of dataLayoutPositionItemKeyPaths) {
    // Get the parent keyPath which would be the array of items
    const itemsKeyPath = getParentKeyPath(itemKeyPath);
    if (!itemsKeyPath) continue;

    const dataLayoutPositionItems = getIn<Identifiable[]>(state, itemsKeyPath);

    // If there are no more items in this position, remove the entire position
    if (!dataLayoutPositionItems || dataLayoutPositionItems.length === 0) {
      // Get the position keyPath (parent of the items array)
      const positionKeyPath = getParentKeyPath(itemsKeyPath);
      if (positionKeyPath) {
        dataLayoutPositionKeyPathsToDelete.push(positionKeyPath);
      }
    }
  }

  // Delete unmapped positions
  if (dataLayoutPositionKeyPathsToDelete.length > 0) {
    state = deleteItems(state, dataLayoutPositionKeyPathsToDelete);
  }

  return state;
}

/**
 * Helper function to add checkboxes to a checkbox group field and update state
 */
export function addCheckboxesToGroup(
  state: TemplateState,
  checkboxFields: TemplateField[],
  checkBoxGroupField: TemplateField,
  isNewGroup = true
): TemplateState {
  // Update field positions to point to the checkbox group
  updateFieldPositions(state, checkboxFields, checkBoxGroupField.id);

  // Update data layout positions
  state = updateDataLayoutPositions(state, checkboxFields, checkBoxGroupField, isNewGroup);

  // Update template field state
  if (isNewGroup) {
    updateTemplateFieldState(state, checkboxFields, checkBoxGroupField);
  } else {
    // Remove the original checkbox fields (now grouped into items) from the template
    const updatedFields = state.template.fields.filter((field) => checkboxFields.every((checkBoxField) => checkBoxField.id !== field.id));
    state.template.fields = updatedFields;
  }

  return state;
}

/**
 * Creates individual fields and data layout positions for ungrouping
 */
export function createIndividualCheckboxFields(items: ItemCollectionItem[], state: TemplateState): TemplateField[] {
  const result: TemplateField[] = [];
  let renameCount = 0;

  const existingNameMap: Record<string, boolean> = {};
  for (const field of state.template.fields) {
    existingNameMap[field.name.toLowerCase()] = true;
  }

  for (const item of items) {
    const displayName = item.displayName;
    let fieldName = displayName;
    let fieldNumber = 0;

    while (existingNameMap[fieldName.toLowerCase()]) {
      fieldNumber++;
      fieldName = `${displayName} ${fieldNumber}`;
    }

    if (fieldNumber > 0) {
      renameCount++;
    }

    existingNameMap[fieldName.toLowerCase()] = true;

    result.push({
      id: item.id,
      name: fieldName,
      type: FieldTypes.CheckBox,
      isFieldCommitted: false,
      isAiPlaced: false
    });
  }

  analyticsTrack('Ungroup Checkbox', {
    'Checkboxes Renamed': renameCount,
    'Checkboxes Ungrouped': items.length
  });

  return result;
}

/**
 * Updates field positions when ungrouping checkbox items
 */
export function updateFieldPositionsWhenUngrouping(state: TemplateState, groupFieldId: string, newIndividualCheckboxFields: TemplateField[]): void {
  const groupFpKeyPaths = getFieldPositionKeyPathsByFieldId(state, groupFieldId);
  for (const groupFpKeyPath of groupFpKeyPaths) {
    const groupFieldPosition = getIn<FieldPosition>(state, groupFpKeyPath);
    if (groupFieldPosition?.itemId) {
      // Find fields that were under the checkbox group
      const correspondingNewField = newIndividualCheckboxFields.find((f) => f.id === groupFieldPosition.itemId);
      if (!!correspondingNewField) {
        // Update the CheckboxGroup FieldPosition to point to the new individual checkbox field
        const updatedFieldPosition: FieldPosition = {
          ...groupFieldPosition,
          fieldId: groupFieldPosition.itemId, // change the former group field ID to the new individual field ID
          itemId: undefined // Clear the itemId as it's no longer under a group
        };
        setIn(state, groupFpKeyPath, updatedFieldPosition);
      }
    }
  }
}

/**
 * Prepares data layout positions for ungrouping by collecting sections to update
 */
export function prepareDataLayoutPositionsForUngrouping(
  state: TemplateState,
  groupFieldId: string
): Map<string, { newDlps: DataLayoutPosition[]; idsToRemove: string[] }> {
  const groupDataLayoutPositionKeyPaths = getDataLayoutPositionKeyPathsByFieldId(state, groupFieldId);
  const sectionsToUpdate = new Map<string, { newDlps: DataLayoutPosition[]; idsToRemove: string[] }>();

  for (const groupDlpKeyPath of groupDataLayoutPositionKeyPaths) {
    const groupDlp = getIn<DataLayoutPosition>(state, groupDlpKeyPath);
    if (!groupDlp) continue;

    const parentSectionPositionsKeyPath = getParentKeyPath(groupDlpKeyPath);
    if (!parentSectionPositionsKeyPath) continue;
    const sectionKeyString = JSON.stringify(parentSectionPositionsKeyPath);

    if (!sectionsToUpdate.has(sectionKeyString)) {
      sectionsToUpdate.set(sectionKeyString, { newDlps: [], idsToRemove: [] });
    }

    sectionsToUpdate.get(sectionKeyString)?.idsToRemove.push(groupDlp.id);
  }

  return sectionsToUpdate;
}

/**
 * Sorts checkbox group items by their sort number
 */
export function sortCheckboxGroupItems(items: ItemCollectionItem[]): ItemCollectionItem[] {
  return [...items]
    .map((item, index) => ({
      ...item,
      sortNumber: item.sortNumber ?? index + 1
    }))
    .sort((a, b) => a.sortNumber - b.sortNumber);
}

/**
 * Creates individual data layout positions for the new checkbox fields
 */
export function createIndividualDataLayoutPositions(groupDlp: DataLayoutPosition, sortedItems: ItemCollectionItem[]): DataLayoutPosition[] {
  const newDlps: DataLayoutPosition[] = [];

  for (const item of sortedItems) {
    const newFieldIdForItem = item.id; // ID of the new individual checkbox field
    const newDlpForItem: DataLayoutPosition = {
      id: getGuid(),
      fieldId: newFieldIdForItem,
      sortNumber: groupDlp.sortNumber + item.sortNumber * 0.0001 // Base new sortNumber on the group's and item's original order
    };
    newDlps.push(newDlpForItem);
  }

  return newDlps;
}

/**
 * Updates section positions with the new data layout positions
 */
export function updateSectionPositionsWithNewDlps(
  state: TemplateState,
  sectionsToUpdate: Map<string, { newDlps: DataLayoutPosition[]; idsToRemove: string[] }>
): TemplateState {
  for (const [sectionKeyString, updateInfo] of sectionsToUpdate.entries()) {
    const sectionPositionsKeyPath = JSON.parse(sectionKeyString) as KeyPath;
    let sectionPositions = getIn<DataLayoutPosition[]>(state, sectionPositionsKeyPath) || [];

    sectionPositions = sectionPositions.filter((p) => !updateInfo.idsToRemove.includes(p.id));
    sectionPositions.push(...updateInfo.newDlps);

    const reorderedSectionPositions = reorderItems(sectionPositions, 'sortNumber');
    setIn(state, sectionPositionsKeyPath, reorderedSectionPositions);
  }

  return state;
}

/**
 * Updates template fields state to replace a checkbox group with individual checkbox fields
 */
export function updateTemplateFieldsAfterUngrouping(state: TemplateState, groupFieldId: string, newIndividualCheckboxFields: TemplateField[]): void {
  // Remove the original CheckBoxGroup field from state
  const updatedFieldsArray = state.template.fields.filter((field) => field.id !== groupFieldId);
  // Add the new individual CheckBox fields
  updatedFieldsArray.push(...newIndividualCheckboxFields);
  state.template.fields = updatedFieldsArray;
  state.selection = {};
}
